import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Grid2 } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import TextFieldComponent from '../../../../components/TextFieldComponent';
import ButtonComponent from '../../../../components/ButtonComponent';
import ModalComponent from '../../../../components/ModalComponent';
import zod from 'zod';
import { CameraFaceGroupFormSchema } from '../../../../services/Recognition/Group/schemas';
import { zodResolver } from '@hookform/resolvers/zod';

import { AppContext } from '../../../../context/AppContextProvider';
import DropdownSelectComponent from '../../../../components/DropdownSelectComponent';
import { IPaginationParams } from '../../../../services/types';
import { IRecognitionGroupProps } from '../../../../services/Recognition/Group/types';
import {
  useCreateRecognitionGroupAsync,
  useUpdateRecognitionGroupAsync,
} from '../../../../services/Recognition/Group/hooks/useMutations';
import {
  IRecognitionCameraFilterProps,
  IRecognitionCameraSimpleProps,
} from '../../../../services/Devices/Recognition/Cameras/Intelbras/RecognitionCameraService/types';
import {
  useGetAllRecognitionCameraByLicenseAsync,
  useGetAllRecognitionCameraByLicenseCountAsync,
  useGetRecognitionCamerasByIdsAsync,
} from '../../../../services/Devices/Recognition/Cameras/Intelbras/RecognitionCameraService/hooks/useMutation';

interface ICameraFaceGroupEditModalProps {
  selectedGroup?: IRecognitionGroupProps;
  isOpen: boolean;
  onClose: () => void;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  handleSaveForm: () => void;
}

type CameraFaceGroupEditionFormInput = zod.infer<
  typeof CameraFaceGroupFormSchema
>;

const RecognitionGroupEditModal = ({
  selectedGroup,
  isOpen,
  onClose,
  setIsOpen,
  handleSaveForm,
}: ICameraFaceGroupEditModalProps) => {
  const { control, reset, handleSubmit, setValue } =
    useForm<CameraFaceGroupEditionFormInput>({
      resolver: zodResolver(CameraFaceGroupFormSchema),
      defaultValues: {
        name: '',
        description: '',
        cameraIds: [],
      },
    });
  const [paginationCameras, setPaginationCameras] = useState<IPaginationParams>(
    {
      pageNumber: 1,
      pageSize: 10,
    },
  );
  const [filterCameras, setFilterCameras] =
    useState<IRecognitionCameraFilterProps>({
      name: '',
    });
  const [missingIds, setMissingIds] = useState<number[]>([]);
  const [cameras, setCameras] = useState<IRecognitionCameraSimpleProps[]>([]);
  const [camerasCount, setCamerasCount] = useState<number>(0);
  const [missingCameras, setMissingCameras] = useState<
    IRecognitionCameraSimpleProps[]
  >([]);
  const { mutateAsync: mutateCreate } = useCreateRecognitionGroupAsync();
  const { mutateAsync: mutateUpdate } = useUpdateRecognitionGroupAsync();
  const { mutateAsync: mutateCameras } =
    useGetAllRecognitionCameraByLicenseAsync();
  const { mutateAsync: mutateCamerasCount } =
    useGetAllRecognitionCameraByLicenseCountAsync();
  const { mutateAsync: mutateMissingCameras } =
    useGetRecognitionCamerasByIdsAsync();
  const { showSnackbar, setIsShowLoadingComponent } = useContext(AppContext);

  const handleEdition = async (data: CameraFaceGroupEditionFormInput) => {
    setIsShowLoadingComponent(true);
    if (selectedGroup && selectedGroup.id) {
      await mutateUpdate({ id: selectedGroup.id, data: data }).then(() => {
        resetAndShowMessage('Grupo facial atulizado com sucesso!');
      });
    } else {
      await mutateCreate(data).then(() => {
        resetAndShowMessage('Grupo facial criado com sucesso!');
      });
    }
    reset();
    setIsOpen(false);
    handleSaveForm();
  };

  const resetAndShowMessage = (message: string) => {
    setIsShowLoadingComponent(false);
    showSnackbar(message, 'success');
    reset();
    setIsOpen(false);
    handleSaveForm();
  };

  const refetchAllCameras = async () => {
    try {
      await mutateCameras({
        pagination: paginationCameras,
        filter: filterCameras,
      }).then(setCameras);
      //await mutateCamerasCount(filterCameras).then(setCamerasCount);
    } catch (error) {
      console.error('Error fetching cameras:', error);
      showSnackbar('Erro ao carregar câmeras', 'error');
    }
  };

  const refetchMissingCameras = async () => {
    if (missingIds && missingIds.length > 0) {
      await mutateMissingCameras(missingIds).then(setMissingCameras);
    }
  };

  const handleOnClose = () => {
    reset();
    onClose();
  };

  const setSelectedCameraIds = () => {
    if (selectedGroup && selectedGroup.id && selectedGroup.cameraGroupIds) {
      const cameraIds = Object.keys(selectedGroup?.cameraGroupIds).map(Number);
      if (cameraIds && cameraIds.length > 0)
        setValue('cameraIds', cameraIds as [number, ...number[]]);
    }
  };

  useEffect(() => {
    refetchAllCameras();
  }, [paginationCameras, filterCameras]);

  useEffect(() => {
    refetchMissingCameras();
  }, [missingIds]);

  useEffect(() => {
    if (selectedGroup && selectedGroup.id) {
      reset(selectedGroup, { keepDefaultValues: true });
      setSelectedCameraIds();
    } else {
      reset();
    }
  }, [selectedGroup]);

  return (
    <ModalComponent
      title={selectedGroup ? 'Editar Grupo Facial' : 'Adicionar Grupo Facial'}
      onClose={handleOnClose}
      isOpen={isOpen}
    >
      <Grid2 container size={12} spacing={3}>
        <Grid2 display={'flex'} justifyContent={'center'} size={12}>
          <Grid2 display={'column'} size={6} justifyContent={'center'}>
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <TextFieldComponent
                  label="Nome"
                  onChangeText={(val) => onChange(val)}
                  type={'text'}
                  errorMessage={error?.message}
                  value={value}
                  maxLength={100}
                />
              )}
              name={'name'}
              control={control}
            />
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <TextFieldComponent
                  label="Descrição"
                  onChangeText={(val) => onChange(val)}
                  type={'text'}
                  errorMessage={error?.message}
                  value={value}
                  maxLength={255}
                />
              )}
              name={'description'}
              control={control}
            />
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <DropdownSelectComponent
                  label={'Câmeras'}
                  labelField={'name'}
                  valueField={'id'}
                  options={cameras ?? []}
                  selectedValues={value}
                  onSelectedValues={(val) => onChange(val)}
                  errorMessage={error?.message}
                  optionsCount={camerasCount}
                  pagination={paginationCameras}
                  setPagination={setPaginationCameras}
                  filter={filterCameras.name}
                  setFilter={setFilterCameras}
                  setMissingIds={setMissingIds}
                  missingItems={missingCameras}
                />
              )}
              name={'cameraIds'}
              control={control}
            />
          </Grid2>
        </Grid2>
        <Grid2 display={'flex'} size={12}>
          <ButtonComponent
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleSubmit(handleEdition)}
          >
            Salvar
          </ButtonComponent>
        </Grid2>
      </Grid2>
    </ModalComponent>
  );
};

export default RecognitionGroupEditModal;
